import React, { useState } from 'react';
import { Mail, ArrowLeft, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ResetPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Couleur principale du thème
  const themeColor = 'rgb(196, 150, 96)';
  const themeColorHover = 'rgb(176, 130, 76)';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setMessage(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/request-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          frontend_url: window.location.origin  // Envoyer le domaine actuel du frontend
        })
      });

      if (response.ok) {
        const data = await response.json();
        setMessage(data.message || 'Si un compte existe avec cet email, vous recevrez un lien de réinitialisation.');
        setEmail('');
      } else {
        // Gérer les erreurs
        try {
          const errorData = await response.json();
          if (typeof errorData.detail === 'string') {
            setError(errorData.detail);
          } else if (Array.isArray(errorData.detail)) {
            // Erreur de validation Pydantic
            setError(errorData.detail.map(err => err.msg).join(', '));
          } else {
            setError('Une erreur est survenue. Veuillez réessayer.');
          }
        } catch {
          setError('Une erreur est survenue. Veuillez réessayer.');
        }
      }
      
    } catch (err) {
      console.error('Erreur:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative" style={{ backgroundColor: '#f8f5f0' }}>
      
      {/* Bouton Retour au site */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 flex items-center space-x-2 px-4 py-2 bg-white border-2 rounded-lg transition-all hover:bg-gray-50 shadow-md"
        style={{ 
          borderColor: themeColor,
          fontFamily: '"Lora", serif'
        }}
      >
        <Home className="w-4 h-4" style={{ color: themeColor }} />
        <span className="text-sm font-medium" style={{ color: themeColor }}>
          Retour au site
        </span>
      </button>

      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
        
        {/* Logo */}
        <div className="text-center">
          <img 
            src="/images/lignages-logo-admin.png" 
            alt="Logo Lignages" 
            className="mx-auto h-16 w-16 object-contain mb-4"
          />
          <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: '"Lora", serif' }}>
            Mot de passe oublié ?
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Entrez votre adresse email pour recevoir un lien de réinitialisation
          </p>
        </div>

        {/* Messages */}
        {message && (
          <div className="border px-4 py-3 rounded-lg" style={{ backgroundColor: 'rgba(196, 150, 96, 0.1)', borderColor: themeColor }}>
            <p className="text-sm" style={{ color: 'rgb(120, 90, 50)' }}>{message}</p>
            <p className="text-xs mt-2" style={{ color: 'rgb(120, 90, 50)' }}>📧 Vérifiez votre boîte de réception (et vos spams)</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Formulaire */}
        {!message && (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Adresse email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent"
                  style={{ '--tw-ring-color': themeColor }}
                  placeholder="votre@email.com"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                style={{ 
                  backgroundColor: isLoading ? themeColorHover : themeColor,
                  fontFamily: '"Lora", serif'
                }}
                onMouseOver={(e) => !isLoading && (e.target.style.backgroundColor = themeColorHover)}
                onMouseOut={(e) => !isLoading && (e.target.style.backgroundColor = themeColor)}
              >
                {isLoading ? (
                  <span>Envoi en cours...</span>
                ) : (
                  <span>Envoyer le lien de réinitialisation</span>
                )}
              </button>
            </div>
          </form>
        )}

        {/* Retour à la connexion */}
        <div className="text-center">
          <button
            onClick={() => navigate('/membre')}
            className="inline-flex items-center text-sm transition-colors"
            style={{ color: themeColor }}
            onMouseOver={(e) => e.target.style.color = themeColorHover}
            onMouseOut={(e) => e.target.style.color = themeColor}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Retour à la connexion
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
